import { useLang } from 'hooks/lang';

import { clientBalancesConfigByCode, clientBalancesStatusConfig, loaders } from 'entities';

import {
  Column,
  Row,
  Skeleton,
  Button,
  DropdownMenu,
  Dot,
  DropdownMenuItem,
} from 'components/Blocks';
import { GoBackButton } from 'components/Buttons';

import NoClient from './NoClient';
import Entries from './Entries';
import Client from './Client';

import { useUrlStateV2 } from 'hooks';

import { ChevronDownIcon } from 'components/Icons';
import { listGet } from 'utils/array';

function ClientsBalance() {
  const { t } = useLang();

  const [values, updateQuery] = useUrlStateV2({
    status: {
      defaultValue: clientBalancesStatusConfig.active.code,
      validate: (value) => !!clientBalancesConfigByCode[value],
    },

    activeBalances: {
      defaultValue: true as boolean,
      parser: (value) => value === 'true',
    },

    client: '',
  });

  const balanceEntries = loaders.clients.balanceEntries.use({
    defaultValue: [],
    blockLoad: !values.client,
    params: {
      id: values.client,
      status: values.status,
    },
  });

  const handleClientChange = async (newClient: string, newStatus?: string) => {
    updateQuery({ client: newClient, status: newStatus ?? values.status });
  };

  const changeBalanceStatus = (status: string) => {
    updateQuery({ status });

    if (values.client) handleClientChange(values.client, status);
  };

  const onlyActive = values.activeBalances;

  return (
    <Column>
      <Column gap="2">
        <h5 className="flex items-center gap-2 text-3xl font-medium text-primary">
          <GoBackButton />

          {t('client-balances')}
        </h5>

        <Row gap="1">
          <DropdownMenu
            align="start"
            trigger={
              <Button variant="ghost" tooltipContent="Status">
                <Dot color={clientBalancesConfigByCode[values.status].color} />
                {clientBalancesConfigByCode[values.status].getLabel(t)}
              </Button>
            }
          >
            {Object.values(clientBalancesStatusConfig).map(({ code, color, getLabel }) => (
              <DropdownMenuItem
                key={code}
                onClick={() => changeBalanceStatus(code)}
                className={code === values.status ? 'text-primary focus:text-primary' : ''}
              >
                <Dot color={color} />
                {getLabel(t)}
              </DropdownMenuItem>
            ))}
          </DropdownMenu>

          <DropdownMenu
            align="start"
            trigger={
              <Button color="gray" variant="ghost" tooltipContent="Client Filter">
                {onlyActive ? t('active-balances') : t('all-clients')}
                <ChevronDownIcon />
              </Button>
            }
          >
            <DropdownMenuItem
              onClick={() => updateQuery({ activeBalances: true })}
              className={onlyActive ? 'text-primary focus:text-primary' : ''}
            >
              {t('active-balances')}
            </DropdownMenuItem>

            <DropdownMenuItem
              onClick={() => updateQuery({ activeBalances: false })}
              className={onlyActive ? '' : 'text-primary focus:text-primary'}
            >
              {t('all-clients')}
            </DropdownMenuItem>
          </DropdownMenu>
        </Row>
      </Column>

      <Client
        client={values.client || ''}
        setClient={handleClientChange}
        showOnlyActive={onlyActive}
        onLoad={(clients) =>
          values.client && !listGet(clients, values.client) && updateQuery({ client: '' })
        }
      />

      {!values.client && <NoClient />}

      {values.client &&
        (balanceEntries.errorOrLoading ? (
          <Skeleton quantity={8} height={42} />
        ) : (
          <Entries entries={balanceEntries.data} setEntries={balanceEntries.overwrite} />
        ))}
    </Column>
  );
}

export default ClientsBalance;
